import React from 'react';
import { styled, useTheme } from '@mui/system';
import { Typography } from '../../atoms/Typography/Typography';
import { Grid, Box } from '@mui/material';
import Check from '@mui/icons-material/Check';
import { updateCurrentStep } from '../../../features/client';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { isStep1Completed } from '../../../common/helper';
import { CLIENT_PROFILE_STAGES } from '../../../common/constants';
import { AdviceGoalPayloadType, removeLoader, showLoader } from '../../../features/client-goals';

interface SingleStepWrapperProps {
    isCompleted?: boolean;
    isCurrent?: boolean;
    cursor?: 'not-allowed' | 'pointer';
}

const HorizontalBar = styled('div')<SingleStepWrapperProps>((props) => ({
    height: '4px',
    flex: 1,
    display: 'flex',
    backgroundColor: props.isCompleted ? props.theme.palette.semantic.bd.brand : props.theme.palette.semantic.bd.default
}));

const Circle = styled('div')<SingleStepWrapperProps>(({ isCompleted, theme, isCurrent, cursor }) => ({
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: isCompleted ? theme.palette.semantic.bg.brand : theme.palette.semantic.bg.default,
    border: `1px solid ${isCurrent ? theme.palette.semantic.bd.brand : theme.palette.semantic.bd.strong}`,
    aspectRatio: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    cursor
}));

const StepperContainer = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    margin: `0 40px`,
    gap: '8px',
    marginBottom: '40px'
});

const EachStep = styled(Box)<{ isMaxWidth?: boolean; showPointer: boolean }>((props) => ({
    display: 'flex',
    alignItems: 'center',
    flex: props.isMaxWidth ? 1 : 'unset',
    width: props.isMaxWidth ? '100%' : '65px',
    gap: '8px',
    position: 'relative',
    cursor: `${props.showPointer ? 'pointer' : 'not-allowed'}`
}));

const StyledContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    // margin: `0 ${theme.spacing(5)}`,
    paddingTop: `${theme.spacing(3)}`,
    maxWidth: '100%'
}));

const StepTextContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    alignItems: 'center'
}));

const StepName = styled('span')<{ isCurrent?: boolean }>(({ isCurrent = false, theme }) => ({
    ...(theme.typography as any).labels?.small,
    color: isCurrent ? theme.palette.semantic.text.brand : theme.palette.semantic.text.muted,
    position: 'absolute',
    top: 32,
    textAlign: 'center',
    whiteSpace: 'nowrap'
}));

interface StepProps {
    name: string;
    isCompleted: boolean;
    stepNumber: number;
    isCurrent: boolean;
    isEnd?: boolean;
    runPlan(): void;
    // eslint-disable-next-line no-unused-vars
    setLastStep(num: any): void;
    goalsWithNoFundingSource?: AdviceGoalPayloadType['formFields'];
    goalsWithMissingDates?: AdviceGoalPayloadType['formFields'];
}

export interface StepperProps {
    steps: { name: string; content: React.ReactElement }[];
    initialStepIndex?: number;
    runPlan(): void;
    goalsWithNoFundingSource?: AdviceGoalPayloadType['formFields'];
    // eslint-disable-next-line no-unused-vars
    setLastStep(num: any): void;
    goalsWithMissingDates?: AdviceGoalPayloadType['formFields'];
}

const Step = ({
    name,
    isCompleted,
    stepNumber,
    isCurrent,
    isEnd,
    runPlan,
    goalsWithNoFundingSource,
    setLastStep,
    goalsWithMissingDates
}: StepProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const step1Complete = isStep1Completed(CLIENT_PROFILE_STAGES, adviceClient);
    const canNavigationToGoals = step1Complete && stepNumber === 2;
    const canNavigateToClient = step1Complete && stepNumber === 1;
    const hasGoalsErrors =
        (goalsWithNoFundingSource && goalsWithNoFundingSource?.length > 0) ||
        (goalsWithMissingDates && goalsWithMissingDates?.length > 0);
    const canNavigationToPlanDetails = adviceGoals.formFields.length > 0 && stepNumber === 3 && !hasGoalsErrors;
    const showPointer = (canNavigationToGoals || canNavigationToPlanDetails || canNavigateToClient) && !isCurrent;

    const onStepperClick = async () => {
        if (isCurrent) return;
        setLastStep(adviceClient.step + 1);
        if (stepNumber === 2 && !step1Complete) {
            return;
        }
        if (stepNumber === 3 && (adviceGoals.formFields.length === 0 || hasGoalsErrors)) return;

        if (
            stepNumber === 3 &&
            adviceClient.step === 1 &&
            adviceGoals.formFields.length !== 0 &&
            adviceGoals.response?.length === 0
        ) {
            dispatch(showLoader());
            await runPlan();
            dispatch(removeLoader());
        }
        await dispatch(updateCurrentStep(stepNumber - 1));
    };

    return (
        <EachStep isMaxWidth={isEnd ? false : true} showPointer={showPointer} onClick={onStepperClick}>
            <StepTextContainer>
                <Circle isCurrent={isCurrent} isCompleted={isCompleted}>
                    {isCompleted ? (
                        <Check sx={{ color: theme.palette.semantic.icon.inverse, marginRight: 0 }} fontSize="inherit" />
                    ) : (
                        <Typography
                            variant="body.small"
                            label={stepNumber}
                            sx={{ color: isCurrent ? theme.palette.semantic.text.brand : 'inherit' }}
                        />
                    )}
                </Circle>

                <StepName isCurrent={isCurrent}>{name}</StepName>
            </StepTextContainer>

            {!isEnd && <HorizontalBar isCompleted={isCompleted} />}
        </EachStep>
    );
};

export const Stepper = ({
    steps,
    initialStepIndex = 0,
    runPlan,
    goalsWithNoFundingSource,
    setLastStep
}: StepperProps) => {
    const [currentStep, setCurrentStep] = React.useState(initialStepIndex);

    return (
        <StyledContainer>
            <StepperContainer>
                {steps.map(({ name }, index) => {
                    return (
                        <Step
                            key={name + index}
                            isCompleted={index < currentStep}
                            isCurrent={index === currentStep}
                            name={name}
                            stepNumber={index + 1}
                            isEnd={index === steps.length - 1}
                            runPlan={runPlan}
                            goalsWithNoFundingSource={goalsWithNoFundingSource}
                            setLastStep={setLastStep}
                        />
                    );
                })}
            </StepperContainer>

            <Grid container item xs={12} direction="column">
                {React.cloneElement(steps[currentStep].content, { updateStep: setCurrentStep })}
            </Grid>
        </StyledContainer>
    );
};
